<template>
  <el-dialog class="print-preview-dialog" :visible.sync="visible" :close-on-click-modal="false" :width="dialogWidth"
    :height="dialogHeight" :custom-class="'print-preview-dialog__custom'" @close="hideModal" append-to-body>
    <template slot="title">
      <div class="preview-title-bar">
        <span class="preview-title-text">打印预览</span>
      </div>
    </template>
    <div v-loading="spinning" class="preview-body">
      <div id="preview_content_editor" class="preview-content"></div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="hideModal">关闭</el-button>
    </span>
  </el-dialog>
</template>

<script>
// 1mm 对应 CSS 像素（按 96dpi 标准）
const MM_TO_PX = 3.779527559
// 弹窗额外边距（左右 padding + 垂直滚动条预留）
const DIALOG_EXTRA_PX = 200

export default {
  name: "printPreview",
  data() {
    return {
      visible: false,
      spinning: true,
      waitShowPrinter: false,
      panelWidthMm: 70,
      panelHeightMm: 40,
      hiprintTemplate: {},
      printData: {}
    }
  },
  computed: {
    dialogWidth() {
      // 参考 vue-plugin-hiprint demo：直接用 editingPanel.width 当 mm 给弹窗，提供充足空间
      const ep = this.hiprintTemplate && this.hiprintTemplate.editingPanel
      const w = ep && ep.width ? parseFloat(ep.width) : (this.panelWidthMm * 2.83)
      const px = Math.round(w * MM_TO_PX + DIALOG_EXTRA_PX)
      // 最小宽度 1000px，最大 95vw
      return Math.min(Math.max(px, 1000), Math.round(window.innerWidth * 0.95)) + 'px'
    },
    dialogHeight() {
      // 参考 vue-plugin-hiprint demo：直接用 editingPanel.width 当 mm 给弹窗，提供充足空间
      const ep = this.hiprintTemplate && this.hiprintTemplate.editingPanel
      const w = ep && ep.height ? parseFloat(ep.height) : (this.panelHeightMm * 2.83)
      const px = Math.round(w * MM_TO_PX + DIALOG_EXTRA_PX)
      // 最小高度 600px，最大 95vw
      return Math.min(Math.max(px, 600), Math.round(window.innerHeight * 0.95)) + 'px'
    }
  },
  methods: {
    hideModal() {
      this.visible = false
    },
    show(hiprintTemplate, printData, width, height) {
      this.visible = true
      this.spinning = true
      this.hiprintTemplate = hiprintTemplate
      this.printData = printData

      // 使用编辑器传入的纸张尺寸（mm）作为最可靠来源
      this.panelWidthMm = width || 70
      this.panelHeightMm = height || 40

      setTimeout(() => {
        try {
          // 获取模板 JSON 并强制所有 scale=1，确保预览为实际打印尺寸
          const templateJson = hiprintTemplate.getJson()
          const cleanConfig = templateJson ? JSON.parse(JSON.stringify(templateJson)) : {}
          const forceScaleOne = (obj) => {
            if (!obj || typeof obj !== 'object') return
            if (Array.isArray(obj)) {
              obj.forEach(item => forceScaleOne(item))
            } else {
              if ('scale' in obj) obj.scale = 1
              Object.values(obj).forEach(val => forceScaleOne(val))
            }
          }
          forceScaleOne(cleanConfig)

          // 创建 scale=1 的临时模板渲染
          const tempTemplate = new (hiprintTemplate.constructor)({ template: cleanConfig })
          const html = tempTemplate.getHtml(printData)

          const container = document.getElementById('preview_content_editor')
          if (container) {
            container.innerHTML = ''
            if (html && typeof html.html === 'function') {
              container.innerHTML = html.html()
            } else if (html instanceof HTMLElement) {
              container.appendChild(html)
            } else if (html && typeof html === 'string') {
              container.innerHTML = html
            } else if (html) {
              container.innerHTML = String(html)
            }
          }
        } catch (e) {
          console.error('预览渲染失败', e)
        }
        this.spinning = false
      }, 300)
    },
    print() {
      if (!this.hiprintTemplate) return
      this.waitShowPrinter = true
      this.hiprintTemplate.print(this.printData, {}, {
        callback: () => {
          this.waitShowPrinter = false
        }
      })
    },
    toPdf() {
      if (!this.hiprintTemplate) return
      this.hiprintTemplate.toPdf(this.printData, '打印预览pdf')
    },
  }
}
</script>

<style lang="scss" scoped>
.print-preview-dialog {
  ::v-deep .el-dialog {
    min-height: 500px;
  }

  ::v-deep .el-dialog__body {
    min-height: 350px;
  }
}

.preview-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 320px;
  white-space: nowrap;

  .preview-title-text {
    font-size: 16px;
    font-weight: bold;
    color: #303133;
    white-space: nowrap;
    margin-right: 16px;
  }

  .preview-title-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }
}
  .el-button {
    min-width: 150px;
    min-height: 40px;    
    margin-left: 0 !important;
    letter-spacing: 1px !important;
    background-color: #fff !important;
    color: #419EFF;
    border-color: #419EFF;
  }
.preview-body {
  padding: 16px;
  min-height: 350px;
  max-height: 70vh;
  overflow: auto;
  background: #f5f7fa;
  display: flex;
  justify-content: center;
  align-items: center;



  .preview-content {
    background: #fff;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);

    // hiprint 输出的 .hiprint-printPaper 自带宽高，不强制限制
    // 防止内部条码/二维码被压扁或溢出
    ::v-deep .hiprint-printPaper {
      background: #fff !important;
    }

    // 隐藏 hiprint 页码、页眉页脚线
    ::v-deep .hiprint-printPaper .hiprint-paperNumber {
      display: none !important;
    }

    ::v-deep .hiprint-footerLine {
      display: none !important;
    }

    ::v-deep .hiprint-headerLine {
      display: none !important;
    }
  }
}
</style>
